// ═══════════════════════════════════════════════════════════════════
// usePageTracking — Frontend hook to record page navigations (v3.3.547)
//
// Mount this once at the App root. It watches React-Router location changes
// and posts each new page to /api/login-history/track-page. Throttled so
// duplicate navigations (e.g. same URL twice in a row) don't double-record.
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "../services/api";

const PAGE_LABELS = {
  "/": "الحجوزات",
  "/clients": "العملاء",
  "/client-groups": "مجموعات العملاء",
  "/marketer-groups": "مجموعات المسوقين",
  "/prospect-groups": "مجموعات المحتملين",
  "/developers": "المطورين",
  "/marketers": "المسوقين",
  "/prospects": "المحتملين",
  "/disbursements": "صرف الدلالات",
  "/contracts": "العقود",
  "/contract-templates": "قوالب العقود",
  "/communication": "التواصل",
  "/communication/clients": "تواصل العملاء",
  "/communication/marketers": "تواصل المسوقين",
  "/communication/business": "التواصل المؤسسي",
  "/projects": "المشاريع",
  "/users": "إدارة المستخدمين",
  "/audit": "التنبيهات",
  "/settings": "الإعدادات",
  "/reservations-scheduler": "تنسيق الحجوزات",
  "/sales": "المبيعات", // v3.4.538
  "/quotations": "عروض الأسعار", // v3.4.561
  "/digital-accounts": "مرجع الحسابات الإلكترونية", // v3.4.547
  "/payments": "المدفوعات المحصّلة",
  "/collections": "إدارة الدفعات والتحصيلات",
  "/analytics": "الإحصائيات",
  "/financial-reports": "التقارير المالية",
  "/commissions": "الدلالات والعمولات",
  "/cash-flow": "التدفقات النقدية",
  "/benchmarking": "مقارنة المشاريع",
  "/company-assets": "أصول الشركة",
  "/capex": "المصاريف الرأسمالية",
  "/sukuk": "التمويل (الصكوك)",
  "/accounting": "البرنامج المحاسبي",
};

function labelForPath(pathname) {
  // Try exact match first
  if (PAGE_LABELS[pathname]) return PAGE_LABELS[pathname];
  // Then try prefix match (for /projects/123, /clients/456, etc)
  for (const [key, label] of Object.entries(PAGE_LABELS)) {
    if (key !== "/" && pathname.startsWith(key + "/")) return label;
    if (key !== "/" && pathname.startsWith(key)) return label;
  }
  return pathname;
}

export default function usePageTracking() {
  const location = useLocation();
  const lastPathRef = useRef(null);
  const lastSentAtRef = useRef(0);

  useEffect(() => {
    const path = location.pathname + (location.search || "");
    const now = Date.now();

    // Skip if same path as last tracked (e.g. re-render or state-only change)
    if (lastPathRef.current === path) return;

    // Throttle: don't track if last call was less than 1 second ago (prevents rapid back/forward storms)
    if (now - lastSentAtRef.current < 1000) return;

    lastPathRef.current = path;
    lastSentAtRef.current = now;

    const title = labelForPath(location.pathname);
    const referrer = document.referrer || null;

    // Fire-and-forget. We don't await — page tracking should never slow navigation.
    api.post("/api/login-history/track-page", {
      path,
      title,
      referrer,
    }).catch(() => {
      // Silent fail — tracking errors must never disrupt user experience
    });
  }, [location.pathname, location.search]);
}
